import {
  ClipboardList,
  BookOpen,
  MessageCircle,
  GraduationCap,
  BookMinusIcon,
  RefreshCw,
} from "lucide-react";
import { tC } from "../../utils/TranslateWithContentLanguage";
import CardImage from "../tool_components/CardImage";
import { Button, Text } from "@core/index";
import FormativeAssessmentCardPDF from "./FormativeAssessmentCardPdf";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import { FormativeAssessmentResponseDto } from "@packages/api/madlenAPI.schemas";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
import { useAppSelector } from "@packages/store/hooks";
import { useState } from "react";
import EditableMarkdown from "../tool_components/EditableMarkdown";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetFormativeAssessmentQueryKey,
  getGetUserFormativeAssessmentsQueryKey,
  updateFormativeAssessment,
} from "@packages/api/madlenAPI";

const FormativeAssessmentCard = ({
  assessment,
  onCreateAgain,
  isEditable = false,
}: {
  assessment: FormativeAssessmentResponseDto;
  isEditable?: boolean;
  onCreateAgain?: () => void;
}) => {
  const finalImageUrl = useImagePdf(assessment.imageUrl);
  const contentLanguage = assessment.contentLanguage || "tr";
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  const queryClient = useQueryClient();

  return (
    <div data-testid="formative-assessment-card" className="min-h-screen">
      <div className="container max-w-7xl mx-auto px-4 py-8">
        {assessment.imageUrl && (
          <CardImage
            createdAt={assessment.createdAt}
            imageUrl={assessment.imageUrl}
            videoUrl={assessment.videoUrl}
            courseName={assessment.courseName}
            gradeLevel={assessment.gradeLevel}
            theme="yellow"
            topic={
              <div className="flex items-center gap-4 justify-center">
                {assessment.title && (
                  <>
                    <div className="p-3 rounded-xl bg-yellow-100 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-400">
                      <BookMinusIcon size={24} />
                    </div>
                    <div>
                      <Text variant={"h1"} className="mb-2">
                        {assessment.title}
                      </Text>
                    </div>
                  </>
                )}
              </div>
            }
            contentLanguage={contentLanguage}
          />
        )}
        <div className="flex justify-end mb-3 gap-2 mt-2">
          {onCreateAgain && (
            <Button
              onClick={onCreateAgain}
              variant="solid"
              size="md"
              textColor="text-white dark:text-white"
              bgColor="bg-gradient-to-r from-yellow-600 to-amber-600 hover:from-yellow-700 hover:to-amber-700 dark:hover:from-yellow-700 dark:hover:to-amber-700"
              rounded="xl"
            >
              <RefreshCw size={18} />
              <span>{tC(contentLanguage, "common.createAnother")}</span>
            </Button>
          )}
          <PdfDownloadButton
            pdfTitle={assessment.title ?? ""}
            openCustomizationModal={() => setShowPdfCustomizationModal(true)}
            document={
              <FormativeAssessmentCardPDF
                customizationSettings={customizationSettings}
                assessment={{ ...assessment, imageUrl: finalImageUrl }}
              />
            }
            btnTheme="bg-gradient-to-r from-yellow-600 to-amber-600"
          />
        </div>

        <div className="space-y-8">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-gray-200/50 dark:border-gray-700/50">
            <div className="space-y-8">
              {/* Process Section */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-xl bg-yellow-100 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-400">
                    <ClipboardList size={20} />
                  </div>
                  <h4 className="font-semibold text-yellow-600 dark:text-yellow-400">
                    {tC(contentLanguage, "formativeAssessment.process")}
                  </h4>
                </div>
                <div className="leading-relaxed">
                  <EditableMarkdown
                    value={assessment.process}
                    isEditable={isEditable}
                    handleSave={async (newValue) => {
                      await updateFormativeAssessment(assessment.id, {
                        process: newValue,
                      });

                      await queryClient.invalidateQueries({
                        queryKey: getGetUserFormativeAssessmentsQueryKey(),
                      });
                      await queryClient.invalidateQueries({
                        queryKey: getGetFormativeAssessmentQueryKey(
                          assessment.id,
                        ),
                      });
                    }}
                  />
                </div>
              </div>

              {/* Assessment Tool Section */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-xl bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400">
                    <BookOpen size={20} />
                  </div>
                  <h4 className="font-semibold text-amber-600 dark:text-amber-400">
                    {tC(contentLanguage, "formativeAssessment.assessmentTool")}
                  </h4>
                </div>
                <div className="leading-relaxed">
                  <EditableMarkdown
                    value={assessment.assessmentTool}
                    isEditable={isEditable}
                    handleSave={async (newValue) => {
                      await updateFormativeAssessment(assessment.id, {
                        assessmentTool: newValue,
                      });

                      await queryClient.invalidateQueries({
                        queryKey: getGetUserFormativeAssessmentsQueryKey(),
                      });
                      await queryClient.invalidateQueries({
                        queryKey: getGetFormativeAssessmentQueryKey(
                          assessment.id,
                        ),
                      });
                    }}
                  />
                </div>
              </div>

              {/* Teacher Feedback Section */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-xl bg-yellow-100 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-400">
                    <MessageCircle size={20} />
                  </div>
                  <h4 className="font-semibold text-yellow-600 dark:text-yellow-400">
                    {tC(contentLanguage, "formativeAssessment.teacherFeedback")}
                  </h4>
                </div>
                <div className="leading-relaxed">
                  <EditableMarkdown
                    value={assessment.teacherFeedback}
                    isEditable={isEditable}
                    handleSave={async (newValue) => {
                      await updateFormativeAssessment(assessment.id, {
                        teacherFeedback: newValue,
                      });

                      await queryClient.invalidateQueries({
                        queryKey: getGetUserFormativeAssessmentsQueryKey(),
                      });
                      await queryClient.invalidateQueries({
                        queryKey: getGetFormativeAssessmentQueryKey(
                          assessment.id,
                        ),
                      });
                    }}
                  />
                </div>
              </div>

              {/* Student Reflection Section */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-xl bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400">
                    <GraduationCap size={20} />
                  </div>
                  <h4 className="font-semibold text-amber-600 dark:text-amber-400">
                    {tC(
                      contentLanguage,
                      "formativeAssessment.studentReflection",
                    )}
                  </h4>
                </div>
                <div className="leading-relaxed">
                  <EditableMarkdown
                    value={assessment.studentReflection}
                    isEditable={isEditable}
                    handleSave={async (newValue) => {
                      await updateFormativeAssessment(assessment.id, {
                        studentReflection: newValue,
                      });

                      await queryClient.invalidateQueries({
                        queryKey: getGetUserFormativeAssessmentsQueryKey(),
                      });
                      await queryClient.invalidateQueries({
                        queryKey: getGetFormativeAssessmentQueryKey(
                          assessment.id,
                        ),
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <FormativeAssessmentCardPDF
            customizationSettings={customizationSettings}
            assessment={{ ...assessment, imageUrl: finalImageUrl }}
          />
        }
      />
    </div>
  );
};

export default FormativeAssessmentCard;
